import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Calendar, BellRing, FileText, Settings, LogOut, BookOpen, Sparkles, Menu, Clapperboard } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BrandLink, NavList } from "@/components/ShellNav";
import { applyTheme, getThemePref, setThemePref } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { useSetting } from "@/lib/appSettings";
import { useT } from "@/lib/i18n";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import LegalFooter from "@/components/LegalFooter";

function trialDaysLeft(trial_until) {
  if (!trial_until) return null;
  const ms = new Date(trial_until).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return null;
  return Math.max(1, Math.ceil(ms / 86400000));
}

const navItems = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/patients", labelKey: "nav.patients", icon: Users, testId: "nav-patients" },
  { to: "/reports", labelKey: "nav.reports", icon: FileText, testId: "nav-reports" },
  { to: "/library", labelKey: "nav.matrix", icon: BookOpen, testId: "nav-library" },
  { to: "/calendar", labelKey: "nav.calendar", icon: Calendar, testId: "nav-calendar" },
  { to: "/reminders", labelKey: "nav.reminders", icon: BellRing, testId: "nav-reminders" },
  { to: "/settings", labelKey: "nav.settings", icon: Settings, testId: "nav-settings" },
];

export default function Shell({ children }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const tx = useT();
  const brandName    = useSetting("app.brand.name", "MIO");
  const brandTagline = useSetting("app.brand.tagline", "Mapping Intelligence for Osteopathy");
  const brandLogo    = useSetting("app.brand.logo_url", "");
  const headerVisible = useSetting("app.header.visible", true);
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    if (user?.theme && user.theme !== getThemePref()) setThemePref(user.theme); else applyTheme();
  }, [user?.theme]);
  const brand = { name: brandName, tagline: brandTagline, logo: brandLogo };
  const sideItems = isAdmin ? [...navItems, { to: "/studio", label: "MIO Studio", icon: Clapperboard, testId: "nav-studio" }] : navItems;
  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside data-testid="app-sidebar" className="hidden md:flex w-64 shrink-0 flex-col sticky top-0 h-screen overflow-y-auto border-r border-slate-800/70 bg-slate-950/80 backdrop-blur-xl">
        <div className="p-6"><BrandLink {...brand} testId="sidebar-brand-link" /></div>
        <div className="flex-1"><NavList items={sideItems} isAdmin={isAdmin} /></div>
        <div className="p-4 m-3 rounded-2xl border border-slate-800 bg-slate-900/50">
          <div className="text-xs text-slate-500">Modo Clínico</div>
          <div className="text-sm text-slate-300 mt-1">Mapeamento biomecânico funcional com apoio de IA.</div>
        </div>
      </aside>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" data-testid="mobile-menu" className="w-[85vw] max-w-xs p-0 bg-slate-950 border-slate-800 text-slate-100 overflow-y-auto z-[60]">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Navegação principal da MIO</SheetDescription>
          <div className="p-6 pr-12"><BrandLink {...brand} testId="mobile-menu-brand-link" onClick={() => setMenuOpen(false)} /></div>
          <NavList items={sideItems} isAdmin={isAdmin} onNavigate={() => setMenuOpen(false)} />
          {user && (
            <button onClick={logout} data-testid="mobile-menu-logout" className="m-3 w-[calc(100%-1.5rem)] flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-xl text-sm text-slate-400 hover:text-slate-100 hover:bg-slate-900/60">
              <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} /> Sair
            </button>
          )}
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {headerVisible && (
        <header data-testid="global-header" className="sticky top-0 z-20 border-b border-slate-800/70 bg-slate-950/80 backdrop-blur-xl pt-safe">
          <div className="flex items-center justify-between px-4 md:px-8 py-3 md:py-4 gap-3 md:gap-4 min-w-0">
            <button type="button" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Abrir menu"
              className="md:hidden shrink-0 inline-flex items-center justify-center w-11 h-11 -ml-1 rounded-xl border border-slate-800 bg-slate-900/60 text-slate-200 hover:text-sky-300">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <BrandLink {...brand} size="header" testId="header-brand-link" />
            </div>
            <div className="hidden md:flex flex-col items-end">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sky-400 font-semibold">Secção</div>
              <div className="font-display text-base text-slate-100">
                {pathname === "/" ? "Painel Clínico" :
                 pathname.startsWith("/patients") ? "Pacientes" :
                 pathname.startsWith("/reports") ? "Relatórios" :
                 pathname.startsWith("/assessments") ? "Espaço de Avaliação" :
                 pathname.startsWith("/calendar") ? "Agenda" :
                 pathname.startsWith("/library") ? "MIO Matrix" :
                 pathname.startsWith("/settings") ? "Definições" :
                 pathname.startsWith("/reminders") ? "Lembretes" : ""}
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3 text-xs text-slate-500">
              <div className="w-2 h-2 rounded-full bg-emerald-400 glow-emerald animate-pulse" />
              <span>Sistema operacional</span>
              {user && (
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-800">
                  <Avatar className="h-8 w-8 ring-1 ring-slate-700">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-slate-800 text-slate-200 text-[10px]">{user.name?.slice(0,2).toUpperCase() || "US"}</AvatarFallback>
                  </Avatar>
                  <div className="text-right">
                    <div className="text-slate-200 text-xs">{user.name}</div>
                    <div className="text-[10px] text-slate-500 capitalize flex items-center gap-1.5 justify-end">
                      {(() => {
                        const left = user.trial_active ? trialDaysLeft(user.trial_until) : null;
                        if (left) return (
                          <span data-testid="trial-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-[9px] uppercase tracking-wider">
                            <Sparkles className="w-2.5 h-2.5" /> Trial Pro · {left}d
                          </span>
                        );
                        return user.plan;
                      })()}
                    </div>
                  </div>
                  <button data-testid="shell-logout-btn" onClick={logout} title="Sair" className="ml-1 p-1.5 rounded-full text-slate-500 hover:text-slate-200 hover:bg-slate-800">
                    <LogOut className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        )}
        <div className="flex-1 px-4 md:px-8 py-6 md:py-10 has-mobile-nav md:!pb-0 min-w-0">{children}</div>
        <LegalFooter />

        {/* Mobile bottom nav — scrollable horizontal + safe-area */}
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-slate-800 bg-slate-950/95 backdrop-blur-xl pb-safe"
          style={{ paddingLeft: "env(safe-area-inset-left, 0px)", paddingRight: "env(safe-area-inset-right, 0px)" }}
        >
          <div className="x-scroll flex items-stretch gap-1 px-2 py-1.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`${item.testId}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 min-w-[68px] px-2 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
                    isActive ? "text-sky-400 bg-sky-500/10" : "text-slate-400 hover:text-slate-200"
                  }`
                }
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="leading-tight">{tx(item.labelKey)}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </main>
    </div>
  );
}
